import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Building2,
  Home,
  Menu,
  Settings,
  X,
  LogOut,
  Moon,
  Sun,
  LayoutDashboard,
  FileText,
  Target,
  MessageSquarePlus,
} from 'lucide-react'

import { FeedbackModal } from '@/components/ui/FeedbackModal'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

import { useAuth } from '@/hooks/use-auth'
import { useTheme } from '@/components/theme-provider'
import { cn } from '@/utils'
import { NotificationBell } from '@/components/notifications/notification-bell'
import { PopupNotificationManager } from '@/components/notifications/popup-manager'
import { investorApi } from '@/services/api'
import { useQuery } from '@tanstack/react-query'

interface DashboardLayoutProps {
  children: React.ReactNode
}

function SubscriptionStatus({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth()
  const [status, setStatus] = useState<any>(null)

  useEffect(() => {
    const fetchStatus = async () => {
      if (!user) return
      try {
        const token = await user.getIdToken()
        const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/subscription/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          setStatus(data)
        }
      } catch (e) {
        console.error("Failed to fetch subscription", e)
      }
    }
    fetchStatus()
  }, [user])

  if (!status) return null

  // Determine color based on plan
  const plan = status.subscription?.plan_name || 'Free'

  const badgeVariants: Record<string, string> = {
    'Enterprise': 'bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-800',
    'Institutional': 'bg-orange-100 text-orange-900 border-orange-200 dark:bg-orange-900/40 dark:text-orange-100 dark:border-orange-800',
    'Professional': 'bg-indigo-100 text-indigo-900 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-100 dark:border-indigo-800',
    'default': 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700'
  }

  const color = badgeVariants[plan] || badgeVariants['default']

  const usage = status.usage
  const isUnlimited = usage?.limit === -1
  const remaining = isUnlimited ? Infinity : Math.max(0, usage?.limit - usage?.used)
  const showUpgrade = !isUnlimited && remaining < 10

  if (compact) {
    return (
      <div className="flex flex-col gap-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-100 dark:border-slate-700/50">
        <div className="flex items-center justify-between">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide border ${color}`}>
            {plan.toUpperCase()}
          </span>
          <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
            {isUnlimited ? `${usage?.used} Used` : `${remaining} Left`}
          </span>
        </div>
        {showUpgrade && (
          <Link to="/plans" className="text-[10px] text-blue-600 dark:text-blue-400 font-medium hover:underline text-center">
            Upgrade Plan
          </Link>
        )}
      </div>
    )
  }

  return (
    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700/50">
      <div className="flex justify-between items-center mb-2">
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wide border ${color}`}>
          {plan.toUpperCase()}
        </span>
      </div>

      <div className="flex justify-between text-[11px] mb-1.5">
        <span className="text-slate-500 dark:text-slate-400 font-medium opacity-90">{isUnlimited ? 'Analyses Used' : 'Analyses Left'}</span>
        <span className={`font-bold ${!isUnlimited && remaining === 0 ? 'text-red-500' : 'text-slate-900 dark:text-white'}`}>
          {isUnlimited ? usage?.used : remaining}
        </span>
      </div>

      {!isUnlimited && (
        <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-3">
          <div
            className={`h-full rounded-full transition-all duration-500 ${remaining > 0 ? 'bg-blue-500' : 'bg-red-500'}`}
            style={{ width: `${Math.min((usage?.used / usage?.limit) * 100, 100)}%` }}
          />
        </div>
      )}

      {showUpgrade && (
        <Link to="/plans" className="block w-full text-center py-1.5 mt-2 text-[10px] font-medium bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm">
          Upgrade Plan
        </Link>
      )}
    </div>
  )
}

const userNavigation = [
  { name: 'Settings', href: '/settings', icon: Settings },
]

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [showThesisHint, setShowThesisHint] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut, loading } = useAuth()
  const { theme, setTheme } = useTheme()

  const isFounder = user?.role === 'founder'

  const founderNav = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Your Startup', href: '/startups/my-startup', icon: Building2 },
    { name: 'Resources', href: '/resources', icon: FileText },
    { name: 'Settings', href: '/settings', icon: Settings },
  ]

  const otherNav = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Your Thesis', href: '/thesis-builder', icon: Target },
    { name: 'Startups', href: '/startups', icon: Building2 },
    // { name: 'Analytics', href: '/analytics', icon: BarChart3 }, // Disabled for now
  ]

  const navigation = isFounder ? founderNav : otherNav

  // Redirect logic: Verify if investor has thesis
  const { data: investorProfile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['investor-profile'],
    queryFn: investorApi.getProfile,
    enabled: !!user && user.role === 'investor',
    retry: false,
  });

  useEffect(() => {
    if (!loading && user && user.role === 'investor' && !isProfileLoading && investorProfile) {
      const hasChecked = sessionStorage.getItem('thesis_check_performed');
      if (hasChecked) return;

      const thesis = (investorProfile as any).thesis;
      const hasThesis = Array.isArray(thesis) && thesis.length > 0;

      if (!hasThesis && location.pathname !== '/thesis-builder') {
        // Show the hint modal instead of redirecting automatically
        setShowThesisHint(true);
      } else {
        sessionStorage.setItem('thesis_check_performed', 'true');
      }
    }
  }, [loading, user, isProfileLoading, investorProfile, location.pathname]);


  const handleLogout = async () => {
    try {
      await signOut()
      navigate('/auth/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-black">
      {/* Global Topbar */}
      <header className="sticky top-0 z-40 w-full border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md">
        <div className="flex h-16 items-center px-4 md:px-6 gap-4 justify-between">

          {/* Logo Section */}
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="uniscout.ai Logo"
                className="h-8 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium flex-1">
            {navigation.map((item) => {
              const isActive = location.pathname.startsWith(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "transition-colors whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800",
                    isActive
                      ? "text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-900/20"
                      : "text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  )}
                >
                  <item.icon className="w-4 h-4" />
                  {item.name}
                </Link>
              )
            })}
          </nav>

          {/* Right Section: Theme, Notification, Profile */}
          <div className="flex items-center gap-2 md:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-slate-600 dark:text-slate-400 hidden sm:flex"
            >
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </Button>

            <Button
              variant="ghost"
              onClick={() => setIsFeedbackOpen(true)}
              title="Share Feedback"
              className="text-indigo-600 dark:text-indigo-400 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 hidden sm:flex items-center gap-1.5 px-3 rounded-full"
            >
              <MessageSquarePlus className="w-4 h-4" />
              <span className="text-sm font-medium">Share Feedback</span>
            </Button>

            <NotificationBell />

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            {/* User Dropdown (Desktop) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="hidden lg:flex h-8 w-8 bg-logo-gradient rounded-full items-center justify-center text-white text-xs font-bold shadow-md cursor-pointer hover:ring-2 ring-offset-2 ring-indigo-500 transition-all select-none" title={user?.displayName || user?.email || 'User'}>
                  {(user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.displayName}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Subscription Status inside Dropdown */}
                {(user?.role === 'investor' || user?.role === 'admin') && (
                  <div className="px-2 pb-2">
                    <SubscriptionStatus compact />
                  </div>
                )}

                <DropdownMenuSeparator />
                {userNavigation.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link to={item.href} className="cursor-pointer">
                      <item.icon className="mr-2 h-4 w-4" />
                      <span>{item.name}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400 cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl absolute w-full left-0 z-50">
            <div className="p-4 space-y-4">
              {/* User Info (Mobile) */}
              <div className="flex items-center gap-3 px-2">
                <div className="w-10 h-10 bg-logo-gradient rounded-full flex items-center justify-center text-white font-bold shadow-inner shrink-0">
                  {(user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{user?.displayName || user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </div>

              {(user?.role === 'investor' || user?.role === 'admin') && (
                <div className="px-2">
                  <SubscriptionStatus />
                </div>
              )}

              <div className="border-t border-slate-100 dark:border-slate-800 my-2" />

              <nav className="space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href)
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                        isActive
                          ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
                          : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  )
                })}
              </nav>

              <div className="border-t border-slate-100 dark:border-slate-800 my-2" />

              <div className="space-y-1">
                {userNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <item.icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                ))}

                <button
                  onClick={() => {
                    toggleTheme();
                    // Don't close menu on theme toggle so user can see effect
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                  {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                </button>

                <button
                  onClick={() => setIsFeedbackOpen(true)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                >
                  <MessageSquarePlus className="w-5 h-5" />
                  Share Feedback
                </button>

                <button
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </div>

            </div>
          </div>
        )}

      </header>

      {/* Main Content */}
      <main className="flex-1 w-full px-4 md:px-6 py-6 fade-in animate-in slide-in-from-bottom-5 duration-500">
        {children}
      </main>

      <FeedbackModal
        open={isFeedbackOpen}
        onOpenChange={setIsFeedbackOpen}
        page="Dashboard"
      />

      {/* Global Popups */}
      <PopupNotificationManager />
      
      {/* Thesis Hint Modal */}
      <Dialog 
        open={showThesisHint} 
        onOpenChange={(open) => {
          if (!open) {
            setShowThesisHint(false);
            sessionStorage.setItem('thesis_check_performed', 'true');
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Set Your Investment Thesis</DialogTitle>
            <DialogDescription>
              You can set your own thesis which defines how your startup thinking looks like. This helps AI tailor the insights for you.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowThesisHint(false);
                sessionStorage.setItem('thesis_check_performed', 'true');
              }}
            >
              Skip for now
            </Button>
            <Button 
              className="bg-logo-gradient text-white"
              onClick={() => {
                setShowThesisHint(false);
                sessionStorage.setItem('thesis_check_performed', 'true');
                navigate('/thesis-builder');
              }}
            >
              Ok, go ahead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
